import { FacebookSettingKey } from '$extensionSrc/recommendations/recommendationSettingsKeys'
import { remoteLog } from '$extensionSrc/utils/remoteLog'

import { FACEBOOK_AUTOMATION_STAGE_STATUSES } from './facebookAutomationStageStatuses'
import {
  getUniquePrivacyLevelCounts,
  allPrivacySettingsFriendsOrStricter,
} from './facebookRecommendationCardDisplayUtils'
import { scrollToBottom } from './fbAlbumPhotoViewUtils'
import * as selectAudienceUtils from './fbSelectAudienceModalUtils'
import { albumsUrl } from './fbUrls'
import { FACEBOOK_PHOTO_STAGE_TIMEOUT } from '../../consts/stageMachineConsts'
import { StageType, FBRecommendationKey } from '../../utils/enums'
import { StageDef } from '../automationMachineInterfaces'
import * as promises from '../interruptiblePromises'
import {
  evaluateXpath,
  getCaseInsensitiveAttribute,
} from '../interruptibleXpath'
import {
  defaultCantUndoFn,
  defaultIsDisabledFn,
  defaultIsDisabledReasonFn,
} from '../recommendationCardDisplayUtils'
import {
  getElement,
  getOptionalElement,
  getInitialValueBySettingKey,
} from '../utils'

export const getCurrentValueFn =
  () =>
  (initialState): string | null => {
    if (initialState === null || initialState === undefined) {
      return null
    }

    if (initialState[FacebookSettingKey.ALBUM_PRIVACY] === null) {
      return 'No albums found'
    }

    if (initialState[FacebookSettingKey.ALBUM_PRIVACY]) {
      return getUniquePrivacyLevelCounts(
        initialState[FacebookSettingKey.ALBUM_PRIVACY],
      )
    }

    return null
  }

export const getIsFulfilledFn =
  () =>
  (initialState): boolean => {
    if (initialState === null || initialState === undefined) {
      return false
    }

    if (initialState[FacebookSettingKey.ALBUM_PRIVACY] === null) {
      return true
    }

    if (initialState[FacebookSettingKey.ALBUM_PRIVACY]) {
      return allPrivacySettingsFriendsOrStricter(
        initialState[FacebookSettingKey.ALBUM_PRIVACY],
      )
    }

    return false
  }

export const getCantUndoFn = () => defaultCantUndoFn

export const getIsDisabledFn = () => defaultIsDisabledFn

export const getIsDisabledReasonFn = () => defaultIsDisabledReasonFn

function getAlbumIdFromHref(href: string): string | null {
  try {
    const url = new URL(href)
    return url.searchParams.get('set')
  } catch {
    return null
  }
}

async function navigateIntoAlbum(albumId: string): Promise<void> {
  const albumElement = await getElement<HTMLAnchorElement>(
    document,
    `//a[contains(@href, '${albumId}')]`,
  )
  albumElement.click()
}

async function getAlbumEditable(albumId: string): Promise<boolean> {
  const albumElement = await getOptionalElement<HTMLAnchorElement>(
    document,
    `//a[contains(@href, '?set=${albumId}') and not(contains(@href, 'fbid'))]`,
  )
  // If the album isn't found, the album is not editable
  if (!albumElement) {
    return false
  }

  const albumMenuButtonEl = await getOptionalElement<HTMLDivElement>(
    albumElement,
    `./following-sibling::div//div[
      contains(
        ${getCaseInsensitiveAttribute('aria-label')},
        'more'
      )
    ]`,
  )
  // If the album menu button isn't found, the album is not editable
  if (!albumMenuButtonEl) {
    return false
  }

  // Click on the album menu button and wait for the album menu to open
  albumMenuButtonEl.click()
  const albumMenuEl = await getOptionalElement<HTMLDivElement>(
    document,
    '//*[@role = "menu"]',
  )
  // If the album menu doesn't open after 5 seconds, the album is not editable
  if (!albumMenuEl) {
    return false
  }

  // Look for an "edit album" option in the album menu. Built-in albums like
  // "profile pictures" and "cover photos" don't have one.
  const editAlbumOptionEl =
    evaluateXpath(albumMenuEl, 'descendant::*[text() = "Edit album"]')[0] ||
    null

  // Click on the album menu button again and wait for the album menu to close
  albumMenuButtonEl.click()
  await promises.getDomPromise(
    document,
    (node) => evaluateXpath(node, '//*[@role = "menu"]').length === 0,
  )

  // If we found an "edit album" menu option, the album is editable
  return editAlbumOptionEl !== null
}

async function getEditableAlbums(
  albumIds: string[],
  processedAlbumIds: Set<string>,
): Promise<Set<string>> {
  const editableAlbumIds: Set<string> = new Set([])

  for (const albumId of albumIds) {
    const albumEditable = await getAlbumEditable(albumId)
    if (albumEditable && !processedAlbumIds.has(albumId)) {
      editableAlbumIds.add(albumId)
    }
  }

  return editableAlbumIds
}

async function openAlbumPrivacyModal(): Promise<void> {
  // If we don't wait a little bit here, the edit audience icon won't be
  // clickable
  await promises.wait(1000)

  const editPrivacyIconEl =
    await promises.getWaitForElementToAppearPromise<HTMLElement>(
      document,
      (node) => selectAudienceUtils.getEditPrivacyIcons(node)[0] || null,
    )

  if (!editPrivacyIconEl) {
    const albumId = getAlbumIdFromHref(window.location.href)

    const message = `openAlbumPrivacyModal: Could not find edit audience icon element for album ID ${albumId}`
    remoteLog.error(message)
    throw new Error(message)
  }

  // Add retry for opening edit audience modal to mitigate update bug
  const modalOpened =
    selectAudienceUtils.clickAndWaitForModalToOpen(editPrivacyIconEl)
  const retryWait = promises.wait(10000)
  const needToRetryClick = await Promise.any([
    modalOpened.then(() => false),
    retryWait.then(() => true),
  ])
  if (needToRetryClick) {
    await selectAudienceUtils.clickAndWaitForModalToOpen(editPrivacyIconEl)
  }
}

async function getAlbumPrivacySetting(): Promise<selectAudienceUtils.PrivacySetting | null> {
  await openAlbumPrivacyModal()

  // Get privacy setting and click cancel
  const privacySetting = await selectAudienceUtils.getSelectedPrivacyLevel()
  await selectAudienceUtils.closeModal('CANCEL', { removeModalManually: true })

  return privacySetting
}

async function setAlbumPrivacySetting(
  targetSetting: selectAudienceUtils.PrivacySetting,
  isDry: boolean,
): Promise<void> {
  await openAlbumPrivacyModal()

  // Select target setting and click done (or cancel if we're in dry run mode)
  await selectAudienceUtils.clickRowMatchingPrivacySetting(targetSetting)
  await selectAudienceUtils.closeModal(isDry ? 'CANCEL' : 'DONE')
}

function getAlbumIdsFromInitialValues(storedInitialValues: any) {
  const initialValues = getInitialValueBySettingKey(
    storedInitialValues,
    FacebookSettingKey.ALBUM_PRIVACY,
  )

  return Object.keys(initialValues)
}

enum StageKey {
  GATHER_SETTINGS_ALBUM_PRIVACY = 'GATHER_SETTINGS_ALBUM_PRIVACY',
  UPDATE_SETTINGS_ALBUM_PRIVACY = 'UPDATE_SETTINGS_ALBUM_PRIVACY',
  UNDO_SETTINGS_ALBUM_PRIVACY = 'UNDO_SETTINGS_ALBUM_PRIVACY',
}

export const gatherStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.GATHER_SETTINGS_ALBUM_PRIVACY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.GATHERING_SETTINGS,
    timeout: FACEBOOK_PHOTO_STAGE_TIMEOUT,
    url: albumsUrl,
    urlPattern: 'https://www.facebook.com/.*sk=photos_albums',
    action: async (_machineContext, userContext, additionalMachineData) => {
      const { initialValues, updateStoredInitialValues } = userContext
      const { resumeData } = additionalMachineData

      const processedAlbumIds: Set<string> = new Set(
        resumeData.data.processedAlbumIds || [],
      )

      let albumIds: string[] = []
      let numTryNoChange: number = 0

      while (numTryNoChange < 3) {
        window.scrollBy(0, 1000)

        await promises.wait(500)

        const visibleAlbumIds = evaluateXpath<HTMLAnchorElement>(
          document,
          "//a[contains(@href, '?set=a.')]",
        )
          .map((x) => getAlbumIdFromHref(x.href))
          .filter((id: string | null) => id !== null)

        if (albumIds.length === visibleAlbumIds.length) {
          numTryNoChange += 1
        } else {
          albumIds = visibleAlbumIds
        }
      }

      const editableAlbumIds = await getEditableAlbums(
        albumIds,
        processedAlbumIds,
      )

      if (editableAlbumIds.size === 0) {
        await updateStoredInitialValues(
          { [FacebookSettingKey.ALBUM_PRIVACY]: null },
          FBRecommendationKey.FB_ALBUM_PRIVACY_V2,
        )
        return
      }

      const gatheredAlbumSettings =
        getInitialValueBySettingKey(
          initialValues,
          FacebookSettingKey.ALBUM_PRIVACY,
        ) || {}

      // Clear deleted albums from initial values
      for (const albumId of getAlbumIdsFromInitialValues(initialValues || {})) {
        if (!editableAlbumIds.has(albumId)) {
          gatheredAlbumSettings[albumId] = undefined

          await updateStoredInitialValues(
            { [FacebookSettingKey.ALBUM_PRIVACY]: gatheredAlbumSettings },
            FBRecommendationKey.FB_ALBUM_PRIVACY_V2,
          )
        }
      }

      for (const albumId of editableAlbumIds) {
        try {
          await navigateIntoAlbum(albumId)
        } catch {
          // If we can't navigate into the album, there's nothing to be done.
          // This shouldn't happen in a gather, but it's good to be consistent.
          // eslint-disable-next-line no-continue
          continue
        }

        const albumPrivacySetting = await getAlbumPrivacySetting()

        if (albumPrivacySetting && albumPrivacySetting.level !== null) {
          gatheredAlbumSettings[albumId] = albumPrivacySetting
          await updateStoredInitialValues(
            {
              [FacebookSettingKey.ALBUM_PRIVACY]: gatheredAlbumSettings,
            },
            FBRecommendationKey.FB_ALBUM_PRIVACY_V2,
          )
        }

        processedAlbumIds.add(albumId)
        resumeData.set({ processedAlbumIds: Array.from(processedAlbumIds) })

        window.history.back()
      }
    },
  },
]

export const updateStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UPDATE_SETTINGS_ALBUM_PRIVACY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UPDATING_SETTINGS,
    timeout: FACEBOOK_PHOTO_STAGE_TIMEOUT,
    url: albumsUrl,
    urlPattern: 'https://www.facebook.com/.*sk=photos_albums',
    action: async (_machineContext, userContext, additionalMachineData) => {
      const {
        incrementTotalCount,
        updateStatisticsLastCount,
        initialValues,
        isDry,
      } = userContext
      const { resumeData } = additionalMachineData

      let ct = resumeData.data.ct || 0

      if (!initialValues) {
        const message = 'updateStageDefs action: No initialValues'
        remoteLog.error(message)
        throw new Error(message)
      }

      let albumIds = getAlbumIdsFromInitialValues(initialValues)

      const processedAlbumIds: Set<string> = new Set(
        resumeData.data.processedAlbumIds || [],
      )
      albumIds = albumIds.filter((albumId) => !processedAlbumIds.has(albumId))

      if (albumIds.length === 0) {
        return
      }

      await scrollToBottom()

      for (const albumId of albumIds) {
        try {
          await navigateIntoAlbum(albumId)
        } catch {
          // If we can't navigate into the album, there's nothing to be done.
          // This can happen in an update if a user deletes an album with an
          // incorrect (e.g. public) setting after the gather but prior to the
          // update.
          // eslint-disable-next-line no-continue
          continue
        }

        const currentAlbumPrivacySetting = await getAlbumPrivacySetting()

        if (
          currentAlbumPrivacySetting &&
          !selectAudienceUtils.isPrivacySettingAsStrictOrStricter(
            currentAlbumPrivacySetting,
            selectAudienceUtils.FRIENDS,
          )
        ) {
          await setAlbumPrivacySetting(selectAudienceUtils.FRIENDS, isDry)

          await incrementTotalCount(FBRecommendationKey.FB_ALBUM_PRIVACY_V2)

          ct += 1
          await updateStatisticsLastCount(
            FBRecommendationKey.FB_ALBUM_PRIVACY_V2,
            ct,
          )
          await resumeData.set({ ct })
        }

        processedAlbumIds.add(albumId)
        resumeData.set({ processedAlbumIds: Array.from(processedAlbumIds) })

        window.history.back()
      }
    },
  },
]

export const undoStageDefs: StageDef[] = [
  {
    type: StageType.ACTION,
    key: StageKey.UNDO_SETTINGS_ALBUM_PRIVACY,
    status: FACEBOOK_AUTOMATION_STAGE_STATUSES.UNDOING_SETTINGS,
    timeout: FACEBOOK_PHOTO_STAGE_TIMEOUT,
    url: albumsUrl,
    urlPattern: 'https://www.facebook.com/.*sk=photos_albums',
    action: async (_machineContext, userContext, additionalMachineData) => {
      const { initialValues: storedInitialValues, isDry } = userContext
      const { resumeData } = additionalMachineData

      const initialValues = getInitialValueBySettingKey(
        storedInitialValues,
        FacebookSettingKey.ALBUM_PRIVACY,
      )

      if (!initialValues) {
        const message = 'undoStageDefs action: No initialValues'
        remoteLog.error(message)
        throw new Error(message)
      }

      let albumIds = getAlbumIdsFromInitialValues(storedInitialValues)

      const processedAlbumIds: Set<string> = new Set(
        resumeData.data.processedAlbumIds || [],
      )
      albumIds = albumIds.filter((albumId) => !processedAlbumIds.has(albumId))

      if (albumIds.length === 0) {
        return
      }

      await scrollToBottom()

      for (const albumId of albumIds) {
        try {
          await navigateIntoAlbum(albumId)
        } catch {
          // If we can't navigate into the album, there's nothing to be done.
          // This can happen in an undo if a user deletes an album with an
          // incorrect (e.g. public) setting after the gather and update but
          // prior to the undo.
          // eslint-disable-next-line no-continue
          continue
        }

        const storedAlbumPrivacySetting = initialValues[albumId]
        const currentAlbumPrivacySetting = await getAlbumPrivacySetting()

        if (
          storedAlbumPrivacySetting?.level !== currentAlbumPrivacySetting?.level
        ) {
          await setAlbumPrivacySetting(storedAlbumPrivacySetting, isDry)
        }

        processedAlbumIds.add(albumId)
        resumeData.set({ processedAlbumIds: Array.from(processedAlbumIds) })

        window.history.back()
      }
    },
  },
]
